import React, { useState, useEffect } from 'react';

const WeatherWidget = ({ city, eventDate }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchWeather = async () => {
      // Clean up city string - remove empty parts like ", " or just state abbreviations
      let cleanCity = city ? city.replace(/^,\s*/, '').replace(/,\s*$/, '').trim() : '';
      
      // Detect country from the city string for international locations
      let detectedCountry = null;
      let detectedCountryCode = null;
      const countryMap = {
        'australia': { name: 'Australia', code: 'AU' },
        'canada': { name: 'Canada', code: 'CA' },
        'united kingdom': { name: 'United Kingdom', code: 'GB' },
        'uk': { name: 'United Kingdom', code: 'GB' },
        'new zealand': { name: 'New Zealand', code: 'NZ' },
        'ireland': { name: 'Ireland', code: 'IE' },
        'germany': { name: 'Germany', code: 'DE' },
        'france': { name: 'France', code: 'FR' },
        'netherlands': { name: 'Netherlands', code: 'NL' },
        'south africa': { name: 'South Africa', code: 'ZA' },
        'jamaica': { name: 'Jamaica', code: 'JM' },
        'trinidad': { name: 'Trinidad and Tobago', code: 'TT' },
        'guyana': { name: 'Guyana', code: 'GY' },
        'philippines': { name: 'Philippines', code: 'PH' },
      };
      
      if (cleanCity) {
        const lowerCity = cleanCity.toLowerCase();
        for (const [key, val] of Object.entries(countryMap)) {
          if (lowerCity.includes(key)) {
            detectedCountry = val.name;
            detectedCountryCode = val.code;
            break;
          }
        }
        // Also detect Australian state abbreviations without explicit country
        const aussieStates = ['nsw', 'vic', 'qld', 'sa', 'wa', 'tas', 'act', 'nt'];
        if (!detectedCountry) {
          const parts = cleanCity.split(',').map(p => p.trim());
          for (const part of parts) {
            if (aussieStates.includes(part.toLowerCase())) {
              detectedCountry = 'Australia';
              detectedCountryCode = 'AU';
              break;
            }
          }
        }
      }
      
      // Handle addresses - extract city/state
      if (cleanCity && cleanCity.includes(',')) {
        const parts = cleanCity.split(',').map(p => p.trim());
        const streetWords = ['st', 'street', 'ave', 'avenue', 'rd', 'road', 'dr', 'drive', 'blvd', 'boulevard', 'ln', 'lane', 'way', 'ct', 'court', 'pl', 'place', 'hwy', 'highway', 'pkwy', 'parkway'];
        const firstPartLower = parts[0].toLowerCase();
        const isAddress = /^\d/.test(parts[0]) || streetWords.some(w => firstPartLower.includes(' ' + w));
        
        // US state abbreviations and full names for filtering
        const stateAbbrs = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];
        const stateNames = ['Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut','Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa','Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan','Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire','New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio','Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota','Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia','Wisconsin','Wyoming'];
        
        // Filter out country names from parts for address parsing
        const filteredPartsForAddress = parts.filter(p => {
          const lower = p.toLowerCase();
          return !countryMap[lower] && lower !== 'united states' && lower !== 'usa' && lower !== 'us';
        });
        
        if (isAddress && filteredPartsForAddress.length >= 2) {
          // Filter out country names, zip codes, and duplicate state names/abbreviations
          let filteredParts = filteredPartsForAddress.filter(p => {
            const cleaned = p.replace(/\s*\d{5}(-\d{4})?$/, '').trim();
            const cleanedLower = cleaned.toLowerCase();
            if (!cleaned) return false;
            if (cleanedLower === 'united states' || cleanedLower === 'usa' || cleanedLower === 'us') return false;
            // Also filter out detected country name
            if (detectedCountry && cleanedLower === detectedCountry.toLowerCase()) return false;
            return true;
          });
          
          // Remove duplicate state entries (e.g. both "RI" and "Rhode Island")
          const stateIndices = [];
          filteredParts.forEach((p, i) => {
            const upper = p.toUpperCase().replace(/\s*\d{5}(-\d{4})?$/, '').trim();
            if (stateAbbrs.includes(upper) || stateNames.some(n => n.toLowerCase() === upper.toLowerCase())) {
              stateIndices.push(i);
            }
          });
          
          // If we have multiple state parts, keep only the first one (abbreviation preferred)
          if (stateIndices.length > 1) {
            const toRemove = stateIndices.slice(1);
            filteredParts = filteredParts.filter((_, i) => !toRemove.includes(i));
          }
          
          // Now identify city and state from remaining parts
          // The street address is the first part (starts with number or has street word)
          // City is the part after the street address, state is after that
          let statePart = '', cityPart = '';
          
          // Find the street address part (first part that looks like an address)
          const addressIdx = filteredParts.findIndex(p => /^\d/.test(p) || streetWords.some(w => p.toLowerCase().includes(' ' + w)));
          
          if (addressIdx !== -1 && filteredParts.length >= addressIdx + 3) {
            // Format: "Street, City, State"
            cityPart = filteredParts[addressIdx + 1].trim();
            statePart = filteredParts[addressIdx + 2].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
          } else if (filteredParts.length >= 2) {
            // Take last part as state, second-to-last as city
            statePart = filteredParts[filteredParts.length - 1].replace(/\s*\d{5}(-\d{4})?$/, '').trim();
            cityPart = filteredParts[filteredParts.length - 2].trim();
          } else if (filteredParts.length === 1) {
            cityPart = filteredParts[0].trim();
          }
          
          // If city part still has street words, try to extract just the city name
          const cityPartLower = cityPart.toLowerCase();
          const isAddressString = /^\d/.test(cityPart);
          if (streetWords.some(w => cityPartLower.includes(' ' + w))) {
            // Find all street word matches and pick the best one
            let bestAfter = '';
            for (const sw of streetWords) {
              const regex = new RegExp('\\b' + sw + '\\b', 'gi');
              let lastMatch;
              let m;
              while ((m = regex.exec(cityPartLower)) !== null) {
                // Skip "st" when followed by a period — it could be "Saint" abbreviated
                // But only skip if it's NOT in an address context (address starts with a digit)
                // In an address like "2760 South 9th St. Salina", "St." is "Street", not "Saint"
                if (sw === 'st' && !isAddressString) {
                  const charAfter = cityPart.substring(m.index + m[0].length);
                  if (charAfter.startsWith('.')) {
                    continue;
                  }
                }
                lastMatch = m;
              }
              if (lastMatch) {
                const afterStreet = cityPart.substring(lastMatch.index + lastMatch[0].length).trim();
                if (afterStreet.length > 0) {
                  bestAfter = afterStreet;
                }
                break;
              }
            }
            if (bestAfter) {
              // Strip leading non-alphanumeric characters (e.g. ". Simons Island" -> "Simons Island")
              cityPart = bestAfter.replace(/^[^a-zA-Z0-9]+/, '').trim();
            }
          }
          
          // If statePart is a full state name, convert to abbreviation for cleaner lookup
          const stateNameIdx = stateNames.findIndex(n => n.toLowerCase() === statePart.toLowerCase());
          if (stateNameIdx !== -1) {
            statePart = stateAbbrs[stateNameIdx];
          }
          
          // Strip trailing periods from state
          statePart = statePart.replace(/\.$/, '');
          
          cleanCity = statePart ? `${cityPart}, ${statePart}` : cityPart;
        }
      }
      
      console.log('Weather widget - looking up:', cleanCity);
      
      if (!cleanCity || cleanCity.length < 2) {
        setLoading(false);
        setError('No location');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Extract just the city name if it's in "City, State" format
        let cityName = cleanCity.split(',')[0].trim();
        // Expand "St." to "Saint" for geocoding API compatibility
        // Only do this when "St." is at the start of the name (city prefix like "St. Louis")
        // Not when it's in the middle of an address (like "9th St. Salina" where St. = Street)
        if (/^St\./.test(cityName)) {
          cityName = cityName.replace(/^St\./, 'Saint');
        }

        // Step 1: Geocode the city to get coordinates using Open-Meteo geocoding
        let geoResponse, geoData;
        
        try {
          const countryCodeParam = detectedCountryCode ? `&country_code=${detectedCountryCode}` : '';
          geoResponse = await fetch(
            `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(cityName)}&count=10&language=en&format=json${countryCodeParam}`
          );
          geoData = await geoResponse.json();
        } catch (err) {
          console.error('Geocoding API error:', err);
          throw new Error('Unable to fetch location data');
        }

        if (!geoData.results || geoData.results.length === 0) {
          // Try with full city string as fallback (also expand St. to Saint)
          try {
            const expandedCleanCity = cleanCity.replace(/^St\./, 'Saint');
            const fallbackResponse = await fetch(
              `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(expandedCleanCity)}&count=5&language=en&format=json`
            );
            const fallbackData = await fallbackResponse.json();
            
            if (!fallbackData.results || fallbackData.results.length === 0) {
              // Try just the first word (city name without state)
              const justCity = cityName.split(' ')[0];
              if (justCity.length >= 3) {
                const lastTryResponse = await fetch(
                  `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(justCity)}&count=10&language=en&format=json`
                );
                const lastTryData = await lastTryResponse.json();
                if (lastTryData.results && lastTryData.results.length > 0) {
                  // Prefer US results
                  const usResult = lastTryData.results.find(r => r.country_code === 'US');
                  geoData.results = usResult ? [usResult] : lastTryData.results;
                } else {
                  throw new Error('Location not found');
                }
              } else {
                throw new Error('Location not found');
              }
            } else {
              geoData.results = fallbackData.results;
            }
          } catch (err) {
            console.error('Fallback geocoding failed:', err);
            throw new Error('Location not found');
          }
        }

        // Try to find the best matching location
        let selectedLocation = geoData.results[0];
        
        if (detectedCountryCode) {
          // For international locations, prefer results in the detected country
          const countryMatch = geoData.results.find(r => r.country_code === detectedCountryCode);
          if (countryMatch) selectedLocation = countryMatch;
        } else if (cleanCity.includes(',')) {
          const stateAbbr = cleanCity.split(',')[1]?.trim().toUpperCase();
          
          // Map common state abbreviations to full names
          const stateMap = {
            'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
            'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
            'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
            'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
            'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
            'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
            'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
            'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
            'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
            'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming'
          };
          
          const stateName = stateMap[stateAbbr] || stateAbbr;
          
          const usLocation = geoData.results.find(r => 
            r.country_code === 'US' && 
            (r.admin1?.toUpperCase() === stateName.toUpperCase() || 
             r.admin1?.toUpperCase().includes(stateAbbr) || 
             r.admin1_code === stateAbbr)
          );
          if (usLocation) selectedLocation = usLocation;
        } else {
          // If no state specified, prefer US results
          const usLocation = geoData.results.find(r => r.country_code === 'US');
          if (usLocation) selectedLocation = usLocation;
        }

        const { latitude, longitude, name, admin1 } = selectedLocation;
        const locationName = `${name}${admin1 ? `, ${admin1}` : ''}`;

        let weatherData;

        // Step 2: Try NWS Weather.gov API for US locations only
        if (selectedLocation.country_code === 'US') {
        try {
          const pointResponse = await fetch(
            `https://api.weather.gov/points/${latitude.toFixed(4)},${longitude.toFixed(4)}`,
            { headers: { 'User-Agent': 'ConferencePlanner (contact@resurgencetechnologygroup.com)' } }
          );
          
          if (pointResponse.ok) {
            const pointData = await pointResponse.json();
            const forecastUrl = pointData.properties.forecast;

            const forecastResponse = await fetch(forecastUrl, {
              headers: { 'User-Agent': 'ConferencePlanner (contact@resurgencetechnologygroup.com)' }
            });
            
            if (forecastResponse.ok) {
              const forecastData = await forecastResponse.json();
              const periods = forecastData.properties.periods;

              if (periods && periods.length > 0) {
                const currentPeriod = periods[0];
                const nextPeriod = periods[1];

                let high, low;
                if (currentPeriod.isDaytime) {
                  high = currentPeriod.temperature;
                  low = nextPeriod?.temperature || currentPeriod.temperature - 15;
                } else {
                  low = currentPeriod.temperature;
                  high = nextPeriod?.temperature || currentPeriod.temperature + 15;
                }

                weatherData = {
                  current: currentPeriod.temperature,
                  high,
                  low,
                  shortForecast: currentPeriod.shortForecast,
                  detailedForecast: currentPeriod.detailedForecast,
                  isDaytime: currentPeriod.isDaytime,
                  windSpeed: currentPeriod.windSpeed,
                  windDirection: currentPeriod.windDirection,
                  probabilityOfPrecipitation: currentPeriod.probabilityOfPrecipitation?.value,
                  location: locationName,
                  periodName: currentPeriod.name,
                  isForecast: true
                };
              }
            }
          }
        } catch (nwsError) {
          console.log('NWS API failed, trying Open-Meteo:', nwsError.message);
        }
        } // end US-only NWS block

        // Step 3: Fallback to Open-Meteo if NWS failed
        if (!weatherData) {
          try {
            const weatherResponse = await fetch(
              `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&daily=temperature_2m_max,temperature_2m_min,weather_code,wind_speed_10m_max,wind_direction_10m_dominant&current_weather=true&timezone=auto`
            );
            
            if (weatherResponse.ok) {
              const weatherJson = await weatherResponse.json();
              
              weatherData = {
                current: Math.round(weatherJson.current_weather.temperature * 9/5 + 32), // Convert C to F
                high: Math.round(weatherJson.daily.temperature_2m_max[0] * 9/5 + 32),
                low: Math.round(weatherJson.daily.temperature_2m_min[0] * 9/5 + 32),
                shortForecast: getWeatherDescription(weatherJson.daily.weather_code[0]),
                detailedForecast: getWeatherDescription(weatherJson.daily.weather_code[0]),
                isDaytime: weatherJson.current_weather.is_day === 1,
                windSpeed: `${Math.round(weatherJson.daily.wind_speed_10m_max[0] * 0.621371)} mph`, // Convert km/h to mph
                windDirection: getWindDirection(weatherJson.daily.wind_direction_10m_dominant[0]),
                probabilityOfPrecipitation: null,
                location: locationName,
                periodName: 'Today',
                isForecast: false
              };
            }
          } catch (fallbackError) {
            console.error('Open-Meteo fallback failed:', fallbackError);
            throw new Error('Weather data unavailable');
          }
        }

        if (!weatherData) {
          throw new Error('Unable to fetch weather data');
        }

        setWeather(weatherData);
      } catch (err) {
        console.error('Weather fetch error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [city, eventDate]);

  const getWeatherIcon = (shortForecast, isDaytime) => {
    if (!shortForecast) return '🌤️';
    const forecast = shortForecast.toLowerCase();
    
    if (forecast.includes('thunder')) return '⛈️';
    if (forecast.includes('snow') || forecast.includes('flurr')) return '🌨️';
    if (forecast.includes('rain') || forecast.includes('shower') || forecast.includes('drizzle')) return '🌧️';
    if (forecast.includes('fog') || forecast.includes('mist')) return '🌫️';
    if (forecast.includes('cloud') || forecast.includes('overcast')) return '☁️';
    if (forecast.includes('partly')) return isDaytime ? '⛅' : '☁️';
    if (forecast.includes('sunny') || forecast.includes('clear')) return isDaytime ? '☀️' : '🌙';
    return isDaytime ? '🌤️' : '🌙';
  };

  const getWeatherDescription = (weatherCode) => {
    const codes = {
      0: 'Clear',
      1: 'Mainly clear',
      2: 'Partly cloudy',
      3: 'Overcast',
      45: 'Fog',
      48: 'Depositing rime fog',
      51: 'Light drizzle',
      53: 'Moderate drizzle',
      55: 'Dense drizzle',
      56: 'Light freezing drizzle',
      57: 'Dense freezing drizzle',
      61: 'Slight rain',
      63: 'Moderate rain',
      65: 'Heavy rain',
      66: 'Light freezing rain',
      67: 'Heavy freezing rain',
      71: 'Slight snow fall',
      73: 'Moderate snow fall',
      75: 'Heavy snow fall',
      77: 'Snow grains',
      80: 'Slight rain showers',
      81: 'Moderate rain showers',
      82: 'Violent rain showers',
      85: 'Slight snow showers',
      86: 'Heavy snow showers',
      95: 'Thunderstorm',
      96: 'Thunderstorm with slight hail',
      99: 'Thunderstorm with heavy hail'
    };
    return codes[weatherCode] || 'Unknown';
  };

  const getWindDirection = (degrees) => {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Weather</h3>
        </div>
        <div className="p-4 text-center">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto mb-4"></div>
            <div className="h-12 bg-gray-200 rounded w-1/2 mx-auto mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Weather</h3>
        </div>
        <div className="p-4 text-center">
          <p className="text-sm text-gray-500">
            Weather data unavailable for this location
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">Weather</h3>
      </div>
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500 mb-1">
          {weather.location}
        </p>
        <p className="text-xs text-gray-400 mb-3">
          {weather.periodName || 'Current Forecast'}
        </p>
        <div className="flex items-center justify-center">
          <div className="text-5xl mr-4">{getWeatherIcon(weather.shortForecast, weather.isDaytime)}</div>
          <div className="text-left">
            <div className="text-2xl font-bold">
              {weather.current}°F
            </div>
            <div className="text-sm text-gray-500">{weather.shortForecast}</div>
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-500">
          <p>
            H: {weather.high}° L: {weather.low}°
            {weather.probabilityOfPrecipitation != null && ` • Precip: ${weather.probabilityOfPrecipitation}%`}
          </p>
          {weather.windSpeed && (
            <p className="mt-1">Wind: {weather.windSpeed} {weather.windDirection}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;
